import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { message } from 'antd';
import dayjs from 'dayjs';

import QuotationService from '../../../../../../../../shared/services/quotation';

import {
  GenericDataFormContext,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../../../shared/contexts';
import { WizardDataFormContext } from './WizardDataFormContext';

import {
  GenerateAPQuotationServiceRequest,
  SelectOption,
} from '../../../../../../../../shared/interfaces';

export const WizardDataFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const { clientSelected } = useContext(StepOneFormContext);
  const { quotationInfo } = useContext(QuotationInfoContext);
  const {
    setIsLoadingPaymentMethods,
    setPaymentMethods,
    setPaymentMethodsList,
  } = useContext(GenericDataFormContext);

  const [quotationForm, setQuotationForm] =
    useState<GenerateAPQuotationServiceRequest>();

  const getPaymentMethodPerVigency = useCallback(
    async (article: string) => {
      const vigencyFrom = dayjs().format('YYYYMMDD');
      const vigencyTo = dayjs().add(1, 'year').format('YYYYMMDD');

      try {
        setIsLoadingPaymentMethods(true);
        const response = await QuotationService.getPaymentPlans(
          article,
          vigencyFrom,
          vigencyTo
        );

        if (response.data?.formasDePago?.length) {
          const paymentMethods: Array<SelectOption> =
            response.data.formasDePago.map(
              ({ descripcion, nroPlanDePago, codigoFormaDePago }) => ({
                label: descripcion,
                value: `${codigoFormaDePago}-${nroPlanDePago}`
              })
            );

          setPaymentMethodsList(response.data.formasDePago);
          setPaymentMethods(paymentMethods);
        }
      } catch {
      } finally {
        setIsLoadingPaymentMethods(false);
      }
    },
    [setPaymentMethods, setPaymentMethodsList]
  );

  const handleTabErrors = useCallback(
    (errorFields: any[], tabPrefixes: string[], tabName: string) => {
      const tabsErrors = new Set<string>();

      errorFields.forEach((fieldError: any) => {
        const errorFieldName = fieldError.name[0];

        const tabIdError = tabPrefixes
          .map((prefix) => errorFieldName.match(new RegExp(`${prefix}-(\\d+)`)))
          .filter(Boolean)
          .map((matches) => matches[1])[0];

        if (tabIdError) tabsErrors.add(tabIdError);
      });

      if (tabsErrors.size > 0) {
        const tabsErrorsArray = Array.from(tabsErrors).join(', ');
        message.error(`Campos faltantes en ${tabName} ${tabsErrorsArray}`);
      }
    },
    []
  );

  useEffect(() => {
    if (clientSelected) {
      setQuotationForm({
        ...quotationForm!,
        cliente: clientSelected,
      });
    }
  }, [clientSelected]);

  useEffect(() => {
    if (quotationInfo) {
      setQuotationForm({
        token: quotationInfo.token,
        rama: quotationInfo.branch,
        articuloRamaSecuencia: quotationInfo.arse,
      });
    }
  }, [quotationInfo]);

  useEffect(() => {
    if (quotationInfo?.article) {
      getPaymentMethodPerVigency(quotationInfo?.article);
    }
  }, [quotationInfo, getPaymentMethodPerVigency]);

  const values = useMemo(
    () => ({
      quotationForm,
      setQuotationForm,
      handleTabErrors,
    }),
    [quotationForm, setQuotationForm, handleTabErrors]
  );

  return (
    <WizardDataFormContext.Provider value={values}>
      {children}
    </WizardDataFormContext.Provider>
  );
};
